import type { IconName } from '../app/navigation';

export type { IconName };

/**
 * Inline 16px stroke icons (no icon dependency, RTL-neutral).
 *
 * Paths are deliberately geometric rather than directional: in an RTL layout a
 * chevron must point the other way, which `Icon` handles via `flip`.
 */
const PATHS: Record<IconName, JSX.Element> = {
  dashboard: (
    <>
      <rect x="2.5" y="2.5" width="5" height="5" rx="1" />
      <rect x="10.5" y="2.5" width="5" height="5" rx="1" />
      <rect x="2.5" y="10.5" width="5" height="5" rx="1" />
      <rect x="10.5" y="10.5" width="5" height="5" rx="1" />
    </>
  ),
  inventory: (
    <>
      <rect x="2" y="3" width="14" height="4" rx="1" />
      <rect x="2" y="9" width="14" height="4" rx="1" />
      <path d="M5 16.5h8" />
    </>
  ),
  device: (
    <>
      <rect x="2" y="4" width="14" height="9" rx="1.5" />
      <path d="M6 16.5h6M9 13v3.5" />
      <circle cx="12.5" cy="8.5" r="1" />
    </>
  ),
  activation: (
    <>
      <path d="M8 1.8l5.5 3v6.4L8 14.2 2.5 11.2V4.8z" />
      <path d="M8 6.5v4M6 8.5h4" />
    </>
  ),
  template: (
    <>
      <rect x="2.5" y="2" width="13" height="14" rx="1.5" />
      <path d="M5.5 6h7M5.5 9h7M5.5 12h4" />
    </>
  ),
  firmware: (
    <>
      <path d="M8 2.2v8M5 7.5l3 3 3-3" />
      <path d="M2.5 13.8h11" />
    </>
  ),
  diagnostics: (
    <>
      <circle cx="7.5" cy="7.5" r="5" />
      <path d="M11.5 11.5l3.5 3.5" />
    </>
  ),
  alarm: (
    <>
      <path d="M8 2.5c-2.5 0-4 1.8-4 4.2 0 3-1 4-1.5 4.6h11C13 10.7 12 9.7 12 6.7c0-2.4-1.5-4.2-4-4.2z" />
      <path d="M6.5 14.2a1.7 1.7 0 003 0" />
    </>
  ),
  olt: (
    <>
      <rect x="1.5" y="5.5" width="15" height="6" rx="1" />
      <path d="M4.5 8.5h2M9 8.5h2.5M4.5 14.5v1.5M13.5 14.5v1.5" />
    </>
  ),
  reconciliation: (
    <>
      <path d="M2.5 6.5A4.5 4.5 0 0111 4.2" />
      <path d="M13.5 11.5A4.5 4.5 0 015 13.8" />
      <path d="M11 2v2.5h-2.5M5 16.5V14h2.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 1.8v2M8 12.2v2M1.8 8h2M12.2 8h2M3.6 3.6l1.4 1.4M11 11l1.4 1.4M12.4 3.6L11 5M5 11l-1.4 1.4" />
    </>
  ),
  external: (
    <>
      <path d="M9.5 2.5h5v5" />
      <path d="M14.5 2.5L7 10" />
      <path d="M12 11v3.5H3.5V6H7" />
    </>
  ),
  search: (
    <>
      <circle cx="7.5" cy="7.5" r="4.8" />
      <path d="M11.2 11.2l3.3 3.3" />
    </>
  ),
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  'chevron-right': <path d="M6.5 3.5L11 8l-4.5 4.5" />,
  'chevron-left': <path d="M9.5 3.5L5 8l4.5 4.5" />,
  'chevron-down': <path d="M3.5 6L8 10.5 12.5 6" />,
  refresh: (
    <>
      <path d="M13.5 8a5.5 5.5 0 11-1.9-4.15" />
      <path d="M13.5 2.5V6H10" />
    </>
  ),
  download: (
    <>
      <path d="M8 2.5v7.5M4.8 7l3.2 3.2L11.2 7" />
      <path d="M2.5 13.5h11" />
    </>
  ),
  power: (
    <>
      <path d="M8 2v6" />
      <path d="M4.2 4.6a5 5 0 107.6 0" />
    </>
  ),
  warning: (
    <>
      <path d="M8 2.2l6 11.3H2z" />
      <path d="M8 6.5v3.5M8 11.6v.4" />
    </>
  ),
  info: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7v4M8 4.4v.4" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4v4.2l3 1.8" />
    </>
  ),
  wifi: (
    <>
      <path d="M2.5 6.5a8 8 0 0111 0" />
      <path d="M4.8 9.2a5 5 0 016.4 0" />
      <path d="M7 11.9a1.6 1.6 0 012 0" />
    </>
  ),
  link: (
    <>
      <path d="M6.5 9.5l3-3" />
      <path d="M8.5 4.5l1.2-1.2a2.5 2.5 0 013.5 3.5L12 8" />
      <path d="M7.5 11.5l-1.2 1.2a2.5 2.5 0 01-3.5-3.5L4 8" />
    </>
  ),
  key: (
    <>
      <circle cx="5.5" cy="10.5" r="3" />
      <path d="M7.8 8.3l5-5M11 5.5l1.5 1.5M9.5 4l1.5 1.5" />
    </>
  ),
  shield: (
    <>
      <path d="M8 1.8l5.2 2v4.4c0 3.2-2.2 5.4-5.2 6.6-3-1.2-5.2-3.4-5.2-6.6V3.8z" />
    </>
  ),
  sparkline: <path d="M2 12l3-4 3 2.5L14 4" />,
};

export interface IconProps {
  name: IconName;
  /** Visual size in px (icons are decorative; pair them with text). */
  size?: number;
  className?: string;
  /** Flip horizontal for RTL reading order (chevrons). */
  flip?: boolean;
  /** Set a title to expose the icon to assistive tech. */
  title?: string;
}

export function Icon({ name, size = 16, className = '', flip = false, title }: IconProps) {
  const glyph = PATHS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {glyph}
    </svg>
  );
}
